import { Check } from "lucide-mui";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DEFAULT_DATE_PRESETS, type DateRangeSpec, presetFrom } from "../specs.ts";

interface DateRangeEditorProps {
  spec: DateRangeSpec;
  from: string;
  to: string;
  onChange: (from: string | null, to: string | null) => void;
}

/** Quick periods ("Last 30 days") and a custom from/to. */
export default function DateRangeEditor({ spec, from, to, onChange }: DateRangeEditorProps) {
  const presets = spec.presets ?? DEFAULT_DATE_PRESETS;
  const activePreset = !to ? presets.find((p) => presetFrom(p) === from) : undefined;
  return (
    <Box>
      <List dense disablePadding sx={{ py: 0.5 }}>
        {presets.map((p) => {
          const selected = p === activePreset;
          return (
            <ListItemButton
              key={p.label}
              onClick={() => onChange(selected ? null : presetFrom(p), null)}
              sx={{ py: 0.25, px: 1.5, gap: 1, minHeight: 32 }}
            >
              <Box sx={{ width: 20, display: "grid", placeItems: "center", color: "primary.main" }}>
                {selected && <Check sx={{ fontSize: 16 }} />}
              </Box>
              <Typography variant="body2" sx={{ fontWeight: selected ? 600 : undefined }}>
                {p.label}
              </Typography>
            </ListItemButton>
          );
        })}
      </List>
      <Divider />
      <Box sx={{ p: 1.5, display: "flex", gap: 1 }}>
        <TextField
          size="small"
          type="date"
          label="From"
          value={from}
          onChange={(e) => onChange(e.target.value || null, to || null)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ flex: 1 }}
        />
        {spec.toParam && (
          <TextField
            size="small"
            type="date"
            label="To"
            value={to}
            onChange={(e) => onChange(from || null, e.target.value || null)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: from || undefined } }}
            sx={{ flex: 1 }}
          />
        )}
      </Box>
    </Box>
  );
}
